import { useMediaQuery } from '@mantine/hooks'

/**
 * Breakpoint values in pixels (matching Mantine theme)
 */
export const BREAKPOINTS = {
  xs: 480,
  sm: 768,
  md: 992,
  lg: 1200,
  xl: 1408,
} as const

export type BreakpointName = keyof typeof BREAKPOINTS

/**
 * Hook to check if current viewport is mobile (< 768px)
 * @returns boolean - true if viewport width is less than sm breakpoint
 */
export function useIsMobile(): boolean {
  return useMediaQuery(`(max-width: ${BREAKPOINTS.sm - 1}px)`) ?? false
}

/**
 * Hook to check if current viewport is tablet (768px - 991px)
 * @returns boolean - true if viewport is tablet size
 */
export function useIsTablet(): boolean {
  return useMediaQuery(`(min-width: ${BREAKPOINTS.sm}px) and (max-width: ${BREAKPOINTS.md - 1}px)`) ?? false
}

/**
 * Hook to check if current viewport is desktop (>= 992px)
 * @returns boolean - true if viewport is desktop size
 */
export function useIsDesktop(): boolean {
  return useMediaQuery(`(min-width: ${BREAKPOINTS.md}px)`) ?? false
}

/**
 * Hook to get the current breakpoint name
 * @returns BreakpointName - current breakpoint ('xs' | 'sm' | 'md' | 'lg' | 'xl')
 */
export function useBreakpoint(): BreakpointName {
  const isXs = useMediaQuery(`(max-width: ${BREAKPOINTS.xs - 1}px)`)
  const isSm = useMediaQuery(`(min-width: ${BREAKPOINTS.xs}px) and (max-width: ${BREAKPOINTS.sm - 1}px)`)
  const isMd = useMediaQuery(`(min-width: ${BREAKPOINTS.sm}px) and (max-width: ${BREAKPOINTS.md - 1}px)`)
  const isLg = useMediaQuery(`(min-width: ${BREAKPOINTS.md}px) and (max-width: ${BREAKPOINTS.lg - 1}px)`)

  if (isXs) return 'xs'
  if (isSm) return 'sm'
  if (isMd) return 'md'
  if (isLg) return 'lg'
  return 'xl'
}

/**
 * Hook to check device orientation
 * @returns 'portrait' | 'landscape'
 */
export function useOrientation(): 'portrait' | 'landscape' {
  const isPortrait = useMediaQuery('(orientation: portrait)')
  return isPortrait ? 'portrait' : 'landscape'
}

/**
 * Hook to check if device supports hover (non-touch)
 * @returns boolean - true if device supports hover
 */
export function useSupportsHover(): boolean {
  return useMediaQuery('(hover: hover)') ?? true
}

/**
 * Hook to check if device prefers reduced motion
 * @returns boolean - true if user prefers reduced motion
 */
export function usePrefersReducedMotion(): boolean {
  return useMediaQuery('(prefers-reduced-motion: reduce)') ?? false
}

/**
 * Hook to get responsive value based on breakpoint
 * @param values - Object with breakpoint keys and values
 * @returns The value for the current breakpoint
 */
export function useResponsiveValue<T>(values: Partial<Record<BreakpointName, T>> & { base: T }): T {
  const breakpoint = useBreakpoint()

  // Check breakpoints from largest to smallest
  const breakpointOrder: BreakpointName[] = ['xl', 'lg', 'md', 'sm', 'xs']
  const currentIndex = breakpointOrder.indexOf(breakpoint)

  // Find the first defined value at or below current breakpoint
  for (let i = currentIndex; i < breakpointOrder.length; i++) {
    const bp = breakpointOrder[i]
    if (values[bp] !== undefined) {
      return values[bp] as T
    }
  }

  return values.base
}

/**
 * Combined responsive info hook
 * @returns Object with all responsive utilities
 */
export function useResponsive() {
  const isMobile = useIsMobile()
  const isTablet = useIsTablet()
  const isDesktop = useIsDesktop()
  const breakpoint = useBreakpoint()
  const orientation = useOrientation()
  const supportsHover = useSupportsHover()
  const prefersReducedMotion = usePrefersReducedMotion()

  return {
    isMobile,
    isTablet,
    isDesktop,
    breakpoint,
    orientation,
    supportsHover,
    prefersReducedMotion,
    // Computed helpers
    isTouchDevice: !supportsHover,
    isSmallScreen: isMobile || isTablet,
    isLargeScreen: isDesktop,
  }
}
